<template>
    <div class="request-card requests-glass argus-glass-page">
        <template v-if="loading && !request">
            <div class="d-flex align-items-center justify-content-center py-5">
                <b-spinner></b-spinner>
                <span class="ml-2">Загрузка заявки...</span>
            </div>
        </template>

        <template v-else-if="!request">
            <b-alert variant="warning" show>
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Не удалось загрузить заявку.
                <b-button variant="outline-secondary" size="sm" class="ml-2" @click="$router.go(-1)">
                    Вернуться назад
                </b-button>
            </b-alert>
        </template>

        <template v-else>
        <div :inert="changesBusy || savingRequest ? '' : null" :aria-busy="changesBusy || savingRequest ? 'true' : 'false'">
        <!-- Заголовок с навигацией -->
        <div class="d-block d-md-flex justify-content-between align-items-center mb-3">
            <div>
                <b-button variant="outline-secondary" @click="goBackToList" class="mr-2">
                    <i class="fas fa-arrow-left"></i> Назад
                </b-button>
                <h4 class="d-inline-block mb-0">Заявка #{{ request.request_number }}</h4>
            </div>
            <div class="mt-2 mt-md-0" v-if="canToggleAdminGodMode">
                <b-button
                    :variant="adminGodModeEnabled ? 'warning' : 'outline-warning'"
                    size="sm"
                    @click="toggleAdminGodMode"
                >
                    <i class="fas fa-user-shield"></i>
                    God Mode
                </b-button>
            </div>
        </div>

        <!-- Кнопки действий (автономный компонент) -->
          <RequestActions
              v-if="!isSubmissionExtension && !isRecurrenceTemplate"
              ref="requestActions"
              :self-assign-preview-enabled="selfAssignPreviewEnabled"
              :save-handler="saveCardChanges"
              :before-action="beforeRequestAction"
              @self-assign-preview-enabled="onSelfAssignPreviewEnabled"
              @saved="onRequestSaved"
              @action-error="requestSaveError = $event"
              @open-materials="showFulfillmentMaterials"
          />

        <RequestRecurrencePanel :request="request" :god-mode="adminGodModeEnabled" @saved="onRequestSaved" />
        <!-- Основная информация о заявке -->
        <b-card class="mb-3 request-glass-card">
            <b-card-header>
                <h5 class="mb-0">{{ request.title }}</h5>
            </b-card-header>
            <b-card-body>
                <!-- Подсказки для участников -->
                <ActionHints v-if="!isSubmissionExtension && !(request.fulfillment && (request.fulfillment.review || ['in_progress', 'partially_completed', 'partially_accepted', 'awaiting_acceptance'].includes(request.status)))" :hints="userActionHints" />

                <!-- Основная информация -->
                <RequestInfo :request="request" />

                <!-- Связанные заявки -->
                <RequestLinks v-if="!isSubmissionExtension" :links="request.linked_requests || []" />

                <!-- Участники процесса -->
                <RequestParticipants
                    :read-only="isSubmissionExtension || isRecurrenceTemplate || (isReportAnnulment && ['completed', 'rejected', 'cancelled'].includes(request.status))"
                    :initiator="initiator"
                    :approvers="approvers"
                    :assigners="assigners"
                    :executors="executors"
                    :observers="observers"
                    :request="request"
                    :admin-god-mode-enabled="adminGodModeEnabled"
                />

                <RequestLifecycle :request="request" />
                <ReportAnnulmentDetails v-if="isReportAnnulment" :request="request" :can-edit="canEditRequestDetails" @saved="onRequestSaved" />

                <TimesheetSubmissionDeadline v-if="isSubmissionExtension" class="mt-3" :request-id="request.id" @saved="onRequestSaved" />

                <CustomFieldsForm
                    v-if="!isSubmissionExtension && hasCustomFields"
                    :fields="customFieldDefinitions"
                    :value="requestCustomFields"
                    :editable="canEditRequestDetails"
                    :request-type-id="request.request_type_id"
                    title="Дополнительные данные"
                    id-prefix="request-card-custom-field"
                    @input="updateCustomFields"
                />

                <!-- Селектор исполнителя (одиночный режим) -->
                <ExecutorSelector v-if="!isSubmissionExtension && !isReportAnnulment && !isMultiExecutor && !selfAssignPreviewEnabled" />

                <!-- Включение режима «несколько исполнителей» и распределение материалов -->
                <MultiExecutorAssign v-if="!isSubmissionExtension && !isReportAnnulment" />

                <!-- Описание -->
                <div
                    v-if="showDescriptionField"
                    class="request-description-panel mt-3"
                    :class="{'request-description-panel--empty': !request.description}"
                >
                    <div class="request-description-header">
                        <div class="request-description-title">
                            <i class="fas fa-align-left"></i>
                            <span>Описание</span>
                        </div>
                        <b-button
                            v-if="canEditRequestDetails"
                            variant="outline-secondary"
                            size="sm"
                            class="request-description-edit-button"
                            :title="descriptionEditing ? 'Закрыть редактирование описания' : 'Редактировать описание'"
                            :aria-label="descriptionEditing ? 'Закрыть редактирование описания' : 'Редактировать описание'"
                            @click="toggleDescriptionEditing"
                        >
                            <i :class="descriptionEditing ? 'fas fa-eye' : 'fas fa-pencil-alt'"></i>
                        </b-button>
                    </div>
                    <div v-if="canEditRequestDetails" v-show="descriptionEditing">
                        <DiscussionEditor
                            field="description" source-type="request_description" :source-id="request.id"
                            ref="descriptionEditor"
                            :value="request.description"
                            @input="updateField('description', $event)"
                            placeholder="Введите описание заявки"
                            class="request-description-editor mb-2"
                        />
                        <small class="text-muted">
                            <i class="fas fa-info-circle"></i>
                            Изменения сохранятся по кнопке «Сохранить изменения» в нижней панели.
                        </small>
                    </div>
                    <DiscussionText
                        v-if="!descriptionEditing && request.description"
                        class="request-description-text"
                        :value="request.description" source-type="request_description" :source-id="request.id"
                    />
                    <div v-else-if="!descriptionEditing" class="request-description-text request-description-text--empty">Описание не указано</div>
                </div>

                <!-- Файлы заявки (автономный компонент) -->
                <RequestFiles @saved="onRequestSaved" />
            </b-card-body>
        </b-card>

        <!-- Материалы с отгрузкой и приемкой -->
        <div class="mb-3" v-if="hasMaterials" ref="materialsSection" tabindex="-1" aria-label="Позиции заявки" style="scroll-margin-top: 6rem">
            <MaterialsTable
                :fulfillment-controlled="Boolean(request.fulfillment)"
                ref="materialsTable"
                :materials="materialItems" 
                :requestId="request.id" 
                :requestTypeId="request.request_type_id"
                :requestBuildingProcessId="request.building_process_id"
                :requestBuildingObjectName="request.building_object_name"
                :catalogLabel="itemCatalogLabel"
                :catalogType="itemCatalogType"
                :allowCustomCatalogItems="allowCustomCatalogItems"
                :canShip="canShipMaterials"
                :canAccept="canAcceptMaterials" 
                :canEditList="canEditMaterialsList" 
                :requestStatus="request.status"
                :isMultiExecutor="isMultiExecutor"
                :executors="executors"
                :canReassignItemExecutor="canReassignItemExecutor"
                :canSelfAssignItemExecutor="canSelfAssignItemExecutor"
                :canSelfReleaseItemExecutor="canSelfReleaseItemExecutor"
                :canSelfAssignBulk="canSelfAssignBulk"
                :canManageFuelCardSelection="canManageFuelCardSelection"
                :canManageFuelCardLimits="canManageFuelCardLimits"
                :canManageMechanicAssetSelection="canManageMechanicAssetSelection"
                :savingRequest="savingRequest"
                :request-save-error="requestSaveError"
                :has-request-changes="hasFormChanges"
                :saved-materials-revision="$store.state.requestCard.materialsSaveRevision"
                :fuelCardOperation="fuelCardOperation"
                :mechanicAssetAction="mechanicAssetAction"
                :mechanicAssetTableMode="mechanicAssetTableMode"
                :selfAssignPreviewEnabled="selfAssignPreviewEnabled"
                :adminGodModeEnabled="adminGodModeEnabled"
                :availableExecutors="availableExecutors"
                @saved="onMaterialsSaved" 
                @save-requested="saveRequestFromMaterialsTable"
                @request-action-blocked="requestSaveError = $event"
                @self-assign-preview-cancelled="onSelfAssignPreviewCancelled"
                @materials-list-changed="onMaterialsListChanged"
                @editing-list-started="onEditingListStarted" 
                @editing-list-cancelled="onEditingListCancelled"
            />
        </div>

        <!-- Позиции заявки (автономный компонент) -->
        <RequestItems />

        <!-- Кнопка скачивания Excel -->
        <div v-if="showItemsExport" class="mt-2 mb-3 d-flex justify-content-end">
            <b-button
                variant="outline-primary"
                size="sm"
                :disabled="downloadingMaterials"
                @click="downloadMaterialsList"
            >
                <b-spinner small v-if="downloadingMaterials" class="mr-1"></b-spinner>
                <i class="fas fa-download" v-if="!downloadingMaterials"></i>
                {{ downloadingMaterials ? 'Скачивание...' : 'Скачать позиции заявки (Excel)' }}
            </b-button>
        </div>

        <!-- Комментарии и история -->
        <CommentsBlock 
            ref="commentsBlock"
            :comments="activityComments" 
            :history="activityHistory" 
            :can-edit="!isSubmissionExtension || !(request.RequestType && request.RequestType.allow_comments === false)"
            :can-add="!isSubmissionExtension || !(request.RequestType && request.RequestType.allow_comments === false)"
            :request-id="request.id"
            @addComment="onAddComment" 
            @editComment="onEditComment" 
            @removeComment="onRemoveComment"
            @removeFile="onRemoveFile" 
        />
        </div>
        <RequestChangesBar :dirty="hasFormChanges" :summary="changesSummary"
            :busy="changesBusy || isAnyActionLoading" :error="requestSaveError" :success="changesSaved"
            @save="saveCardChanges" @discard="askAboutRequestChanges('discard')" @dismiss="changesSaved = false" />
        </template>
        <b-modal v-model="showChangesDialog" title="Несохранённые изменения" centered
            content-class="argus-glass-dialog" :no-close-on-backdrop="true" :no-close-on-esc="changesBusy"
            :hide-header-close="changesBusy" @hidden="finishChangesDialog(false)">
            <p v-if="changesIntent === 'discard'">Отменить несохранённые правки заявки? Уже сохранённые данные останутся.</p>
            <p v-else-if="changesIntent === 'action'">Перед выполнением действия сохраните изменения заявки или отмените их.</p>
            <p v-else>В заявке остались несохранённые изменения.</p>
            <p v-if="hasFormChanges" class="mb-0">{{ changesSummary.join(' · ') }}</p>
            <b-alert v-if="separateDrafts" show variant="warning" class="mt-3 mb-0">
                В комментарии или отдельном редакторе остался ввод. Он не сохраняется вместе с заявкой.
                Останьтесь на странице, чтобы завершить работу; при выходе этот ввод будет потерян.
            </b-alert>
            <template #modal-footer>
                <div class="w-100">
                    <div v-if="changesDialogError" role="alert" class="alert alert-danger">{{ changesDialogError }}</div>
                    <div class="d-flex flex-wrap justify-content-end" style="gap: 8px">
                        <b-button size="sm" variant="outline-secondary" :disabled="changesBusy" @click="finishChangesDialog(false)">Остаться</b-button>
                        <b-button size="sm" variant="outline-danger" :disabled="changesBusy" @click="resolveChangesDialog(false)">
                            {{ changesIntent === 'leave' ? 'Выйти без сохранения' : changesIntent === 'action' ? 'Отменить правки и продолжить' : 'Отменить изменения' }}
                        </b-button>
                        <b-button v-if="changesIntent !== 'discard' && !separateDrafts" size="sm" variant="success" :disabled="changesBusy || changesRefreshRequired" @click="resolveChangesDialog(true)">
                            {{ changesBusy ? 'Подождите…' : changesIntent === 'leave' ? 'Сохранить и выйти' : 'Сохранить и продолжить' }}
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import ReportAnnulmentDetails from '../components/ReportAnnulmentDetails.vue';
import RequestRecurrencePanel from '../components/RequestRecurrencePanel.vue';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import CommentsBlock from './CommentsBlock/CommentsBlock.vue';
import MaterialsTable from './MaterialsTable/MaterialsTable.vue';
import RequestActions from './components/RequestActions.vue';
import RequestChangesBar from './components/RequestChangesBar.vue';
import pendingChanges from './pendingChanges';
import TimesheetSubmissionDeadline from '@/views/Timesheets/TimesheetSubmissionDeadline.vue';
import RequestInfo from './components/RequestInfo.vue';
import RequestParticipants from './components/RequestParticipants.vue';
import ExecutorSelector from './components/ExecutorSelector.vue';
import MultiExecutorAssign from './components/MultiExecutorAssign.vue';
import ActionHints from './components/ActionHints.vue';
import RequestFiles from './components/RequestFiles.vue';
import RequestItems from './components/RequestItems.vue';
import RequestLinks from './components/RequestLinks.vue';
import RequestLifecycle from './components/RequestLifecycle.vue';
import CustomFieldsForm from '../components/CustomFieldsForm.vue';
import { markRequestOpened } from '@/utils/requestViewedStorage';
import {updateRecentWorkItem} from '@/utils/recentWork';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

export default {
    name: "RequestCard",
    mixins: [pendingChanges],
    components: {
        RequestChangesBar,
        ReportAnnulmentDetails,
        RequestRecurrencePanel,
        TimesheetSubmissionDeadline,
        CommentsBlock,
        MaterialsTable,
        RequestActions,
        RequestInfo,
        RequestParticipants,
        ExecutorSelector,
        MultiExecutorAssign,
        ActionHints,
        RequestFiles,
        RequestItems,
        RequestLinks,
        RequestLifecycle,
        CustomFieldsForm
    },
    data() {
        return {
            telegramBackButtonHandler: null,
            telegramHistoryBackInProgress: false,
            selfAssignPreviewEnabled: false,
            descriptionEditing: false,
            requestSaveError: ''
        };
    },
    computed: {
        isReportAnnulment() { return this.request && this.request.workflow_data && this.request.workflow_data.domain === 'report_annulment'; },
        isRecurrenceTemplate() { return Boolean(this.request && this.request.workflow_data && this.request.workflow_data.recurrence && this.request.workflow_data.recurrence.kind === 'template'); },
        isSubmissionExtension() { return Boolean(this.request && this.request.workflow_data && this.request.workflow_data.domain === 'timesheet_submission_extension'); },
        canEditRequestDetails() { return !this.isSubmissionExtension && this.$store.getters['requestCard/canEditRequestDetails']; },
        ...mapState('requestCard', [
            'request',
            'loading',
            'savingRequest',
            'downloadingMaterials',
            'availableExecutors',
            'adminGodModeEnabled'
        ]),
        ...mapGetters('requestCard', [
            'activityComments',
            'activityHistory',
            'canEditMaterialsList',
            'canShipMaterials',
            'canAcceptMaterials',
            'initiator',
            'approvers',
            'executors',
            'assigners',
            'observers',
            'hasMaterials',
            'materialItems',
            'userActionHints',
            'hasFormChanges',
            'isAnyActionLoading',
            'isMultiExecutor',
            'canReassignItemExecutor',
            'canSelfAssignItemExecutor',
            'canSelfReleaseItemExecutor',
            'canSelfAssignBulk',
            'canManageFuelCardSelection',
            'canManageFuelCardLimits',
            'canManageMechanicAssetSelection'
        ]),
        itemCatalogLabel() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const itemCatalog = workflowConfig && workflowConfig.itemCatalog ? workflowConfig.itemCatalog : null;

            if (itemCatalog && itemCatalog.enabled && itemCatalog.label) {
                return itemCatalog.label;
            }

            if (requestType && requestType.category === 'fuelCards') {
                return 'Топливные карты';
            }

            if (requestType && requestType.category === 'mechanics') {
                return 'Техника';
            }

            return 'Материалы';
        },
        itemCatalogType() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const itemCatalog = workflowConfig && workflowConfig.itemCatalog ? workflowConfig.itemCatalog : null;

            if (itemCatalog && itemCatalog.enabled && itemCatalog.type) {
                return itemCatalog.type;
            }

            if (requestType && requestType.category === 'fuelCards') {
                return 'fuel_cards';
            }

            if (requestType && requestType.category === 'mechanics') {
                return 'mechanic_assets';
            }

            return 'materials';
        },
        allowCustomCatalogItems() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const itemCatalog = workflowConfig && workflowConfig.itemCatalog ? workflowConfig.itemCatalog : null;

            if (this.itemCatalogType === 'employees') {
                return Boolean(itemCatalog && (
                    itemCatalog.allow_custom_items === true ||
                    itemCatalog.allowCustomItems === true ||
                    itemCatalog.allow_manual_items === true ||
                    itemCatalog.allowManualItems === true
                ));
            }

            return this.itemCatalogType === 'materials';
        },
        isEmployeeCatalog() {
            return this.itemCatalogType === 'employees';
        },
        isFuelCardCatalog() {
            return this.itemCatalogType === 'fuel_cards';
        },
        isMechanicAssetCatalog() {
            return this.itemCatalogType === 'mechanic_assets';
        },
        fuelCardOperation() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const source = workflowConfig.fuelCardAction || workflowConfig.fuel_card_action || {};
            return source.operation || 'assign_to_initiator';
        },
        mechanicAssetAction() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const actions = Array.isArray(workflowConfig.mechanicAssetActions || workflowConfig.mechanic_asset_actions)
                ? (workflowConfig.mechanicAssetActions || workflowConfig.mechanic_asset_actions)
                : [];
            const enabledActions = actions.filter(action => action && action.enabled === true);
            const preferredTriggerStatus = this.request && this.request.status === 'awaiting_executor'
                ? 'in_progress'
                : (this.request && this.request.status);
            const source = enabledActions.find(action => action.triggerStatus === preferredTriggerStatus) ||
                enabledActions[0] ||
                workflowConfig.mechanicAssetAction ||
                workflowConfig.mechanic_asset_action ||
                {};
            return {
                enabled: source.enabled === true,
                operation: source.operation || 'move_to_object',
                requireMeterValue: source.requireMeterValue === true || source.require_meter_value === true,
                requireCondition: source.requireCondition === true || source.require_condition === true
            };
        },
        mechanicAssetTableMode() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            return workflowConfig.mechanicAssetTableMode ||
                workflowConfig.mechanic_asset_table_mode ||
                'movement';
        },
        showItemsExport() {
            const items = this.request && Array.isArray(this.request.items) ? this.request.items : [];
            if (!items.length) {
                return false;
            }

            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const excelExport = workflowConfig.excelExport || workflowConfig.excel_export || {};
            return excelExport.enabled !== false && excelExport.enabled !== 'false';
        },
        descriptionFieldConfig() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const workflowConfig = requestType && requestType.workflow_config ? requestType.workflow_config : {};
            const config = workflowConfig && workflowConfig.descriptionField ? workflowConfig.descriptionField : {};
            return {
                enabled: config.enabled !== false
            };
        },
        showDescriptionField() {
            return this.descriptionFieldConfig.enabled;
        },
        customFieldDefinitions() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            const fields = requestType && Array.isArray(requestType.custom_fields)
                ? requestType.custom_fields
                : [];
            return fields.filter(field =>
                field &&
                String(field.key || '').trim() &&
                String(field.label || '').trim()
            );
        },
        hasCustomFields() {
            return this.customFieldDefinitions.length > 0;
        },
        requestCustomFields() {
            const workflowData = this.request && this.request.workflow_data
                ? this.request.workflow_data
                : {};
            const values = workflowData.custom_fields || workflowData.customFields || {};
            return values && typeof values === 'object' && !Array.isArray(values) ? values : {};
        },
        canToggleAdminGodMode() {
            const adminPermissions = this.request && this.request.adminPermissions
                ? this.request.adminPermissions
                : null;

            return this.request &&
                this.request.isAdmin === true &&
                (
                    (adminPermissions && adminPermissions.canManageRequestParticipants === true) ||
                    (
                        this.request.permissions &&
                        this.request.permissions.canManageRequestParticipants === true
                    )
                );
        },
        currentUser() {
            return this.$store.getters.currentUser;
        },
        isTelegramWebApps() {
            return this.$store.getters.isTelegramWebApps;
        },
        shouldSkipAutoRefresh() {
            if (this.loading || this.isAnyActionLoading || this.changesBusy || this.showChangesDialog) {
                return true;
            }
            if (this.hasFormChanges) {
                return true;
            }
            if (this.$store.state.requestCard.selectedExecutorId) {
                return true;
            }

            const materialsTable = this.$refs.materialsTable;
            if (materialsTable && typeof materialsTable.hasDirtyState === 'function' && materialsTable.hasDirtyState()) {
                return true;
            }

            const commentsBlock = this.$refs.commentsBlock;
            if (commentsBlock && typeof commentsBlock.hasDirtyState === 'function' && commentsBlock.hasDirtyState()) {
                return true;
            }

            const requestActions = this.$refs.requestActions;
            return Boolean(requestActions && typeof requestActions.hasDirtyState === 'function' && requestActions.hasDirtyState());
        }
    },
    methods: {
        async showFulfillmentMaterials() {
            await this.$nextTick();
            const section = this.$refs.materialsSection;
            if (!section) return;
            section.focus({preventScroll: true});
            section.scrollIntoView({behavior: 'smooth', block: 'start'});
        },
        ...mapActions('requestCard', [
            'requestCard_fetchRequest',
            'requestCard_downloadMaterialsList',
            'requestCard_addComment',
            'requestCard_updateComment',
            'requestCard_deleteComment',
            'requestCard_deleteCommentFile',
            'requestCard_handleMaterialsChange',
            'requestCard_startEditingMaterialsList',
            'requestCard_cancelEditingMaterialsList',
            'requestCard_updateField',
            'requestCard_reset',
            'requestCard_autoSaveDraft'
        ]),
        ...mapMutations('requestCard', [
            'setAdminGodModeEnabled'
        ]),

        
        goBackToList(options = {}) {
            const id = this.request && this.request.id ? this.request.id : this.$route.params.id;
            const route = { path: '/requests', query: id ? { highlight: String(id) } : {} };
            const method = options && options.replace ? 'replace' : 'push';

            return this.$router[method](route).catch(() => {});
        },

        getTelegramBackButton() {
            return window.Telegram
                && window.Telegram.WebApp
                && window.Telegram.WebApp.BackButton
                ? window.Telegram.WebApp.BackButton
                : null;
        },

        setupTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === 'undefined') {
                return;
            }

            this.telegramBackButtonHandler = () => {
                this.goBackToList({ replace: true });
            };

            const backButton = this.getTelegramBackButton();
            if (backButton) {
                if (typeof backButton.onClick === 'function') {
                    backButton.onClick(this.telegramBackButtonHandler);
                }
                if (typeof backButton.show === 'function') {
                    backButton.show();
                }
            }

            window.addEventListener('popstate', this.handleTelegramHistoryBack);
            this.pushTelegramHistorySentinel();
        },

        teardownTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === 'undefined') {
                return;
            }

            const backButton = this.getTelegramBackButton();
            if (
                backButton
                && this.telegramBackButtonHandler
                && typeof backButton.offClick === 'function'
            ) {
                backButton.offClick(this.telegramBackButtonHandler);
            }
            if (backButton && typeof backButton.hide === 'function') {
                backButton.hide();
            }

            window.removeEventListener('popstate', this.handleTelegramHistoryBack);
            this.telegramBackButtonHandler = null;
        },

        pushTelegramHistorySentinel() {
            try {
                const state = window.history.state || {};
                if (state && state.requestCardTelegramBack) {
                    return;
                }
                window.history.pushState(
                    {
                        ...state,
                        requestCardTelegramBack: true,
                        requestCardId: String(this.$route.params.id || '')
                    },
                    document.title,
                    window.location.href
                );
            } catch (error) {
                console.error('Не удалось подготовить Telegram back navigation:', error);
            }
        },

        handleTelegramHistoryBack() {
            if (
                !this.isTelegramWebApps
                || this.telegramHistoryBackInProgress
                || this.$route.name !== 'request-card'
            ) {
                return;
            }

            this.telegramHistoryBackInProgress = true;
            this.goBackToList({ replace: true })
                .finally(() => {
                    this.telegramHistoryBackInProgress = false;
                });
        },

        async fetchRequest() {
            const id = this.$route.params.id;
            try {
                await this.requestCard_fetchRequest(id);
                const selfAssignPreviewStatuses = ['awaiting_executor', 'in_progress'];
                if (
                    this.isMultiExecutor ||
                    (this.request && !selfAssignPreviewStatuses.includes(this.request.status))
                ) {
                    this.selfAssignPreviewEnabled = false;
                }
                this.markCurrentRequestOpened();
            } catch (error) {
                this.$bvToast && this.$bvToast.toast('Не удалось загрузить заявку', { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },
        markCurrentRequestOpened() {
            if (this.request && this.request.id) {
                markRequestOpened(this.request.id, this.currentUser);
                const requestNumber = this.request.request_number || this.request.id;
                updateRecentWorkItem(
                    this.currentUser,
                    'request',
                    this.$route.params.id,
                    {
                        label: this.request.title || '',
                        contextLabel: `Заявка #${requestNumber}`
                    }
                );
            }
        },
        async autoRefreshOnResume() {
            if (document.hidden || !this.request || !this.request.id || this.shouldSkipAutoRefresh) {
                return;
            }

            try {
                await this.fetchRequest();
            } catch (error) {
                console.error('Не удалось автообновить карточку заявки:', error);
            }
        },
        handleVisibilityChange() {
            if (!document.hidden) {
                this.autoRefreshOnResume();
            }
        },

        updateField(field, value) {
            this.requestCard_updateField({ field, value });
        },
        updateCustomFields(values) {
            const workflowData = {
                ...(this.request && this.request.workflow_data ? this.request.workflow_data : {}),
                custom_fields: values || {}
            };
            this.updateField('workflow_data', workflowData);
        },

        toggleDescriptionEditing() {
            this.descriptionEditing = !this.descriptionEditing;

            if (this.descriptionEditing) {
                this.$nextTick(() => {
                    const editor = this.$refs.descriptionEditor;
                    if (editor && typeof editor.focus === 'function') {
                        editor.focus();
                    }
                });
            }
        },

        toggleAdminGodMode() {
            this.setAdminGodModeEnabled(!this.adminGodModeEnabled);
        },

        onMaterialsListChanged(changes) {
            this.requestCard_handleMaterialsChange(changes);
        },

        onEditingListStarted() {
            this.requestCard_startEditingMaterialsList();
        },

        onEditingListCancelled() {
            this.requestCard_cancelEditingMaterialsList();
        },

        onSelfAssignPreviewEnabled() {
            this.selfAssignPreviewEnabled = true;
            this.$nextTick(() => {
                const materialsTable = this.$refs.materialsTable;
                const target = materialsTable && materialsTable.$el
                    ? materialsTable.$el
                    : materialsTable;

                if (target && typeof target.scrollIntoView === 'function') {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },

        onSelfAssignPreviewCancelled() {
            this.selfAssignPreviewEnabled = false;
        },

        async onMaterialsSaved() {
            notifyInstallPromotionSuccess({source: 'request-items-updated'});
            await this.fetchRequest();
        },

        async onAddComment(data) {
            try {
                await this.requestCard_addComment(data);
                if (data.done) data.done();
                notifyInstallPromotionSuccess({source: 'request-comment-added'});
                this.$bvToast && this.$bvToast.toast('Комментарий добавлен', { 
                    title: 'Успех', 
                    variant: 'success', 
                    solid: true 
                });
            } catch (error) {
                if (data.done) data.done(error);
                this.$bvToast && this.$bvToast.toast('Не удалось добавить комментарий', {
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },

        async onEditComment(data) {
            try {
                await this.requestCard_updateComment(data);
                notifyInstallPromotionSuccess({source: 'request-comment-edited'});
                this.$bvToast && this.$bvToast.toast('Комментарий обновлен', { 
                    title: 'Успех', 
                    variant: 'success', 
                    solid: true 
                });
            } catch (error) {
                this.$bvToast && this.$bvToast.toast('Не удалось обновить комментарий', { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },

        async onRemoveComment(commentId) {
            try {
                await this.requestCard_deleteComment(commentId);
                notifyInstallPromotionSuccess({source: 'request-comment-deleted'});
                this.$bvToast && this.$bvToast.toast('Комментарий удален', { 
                    title: 'Успех', 
                    variant: 'success', 
                    solid: true 
                });
            } catch (error) {
                this.$bvToast && this.$bvToast.toast('Не удалось удалить комментарий', { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },

        async onRemoveFile(data) {
            try {
                await this.requestCard_deleteCommentFile(data);
                notifyInstallPromotionSuccess({source: 'request-comment-file-deleted'});
                this.$bvToast && this.$bvToast.toast('Файл удален', { 
                    title: 'Успех', 
                    variant: 'success', 
                    solid: true 
                });
            } catch (error) {
                this.$bvToast && this.$bvToast.toast('Не удалось удалить файл', { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },

        async downloadMaterialsList() {
            try {
                await this.requestCard_downloadMaterialsList();
                this.$bvToast && this.$bvToast.toast('Файл успешно скачан', {
                    title: 'Успех',
                    variant: 'success',
                    solid: true
                });
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось скачать позиции заявки';
                this.$bvToast && this.$bvToast.toast(errorMsg, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },
      async saveRequestFromMaterialsTable() {
        await this.saveCardChanges();
      },
      async onRequestSaved(result) {
        if (!result || !result.refreshed) await this.fetchRequest();
        this.descriptionEditing = false;

        this.$nextTick(() => {
          const mt = this.$refs.materialsTable;
          if (mt && typeof mt.finishEditingListAfterSave === 'function') {
            mt.finishEditingListAfterSave();
          }
        });
      }
    },
    watch: {
        '$route.params.id': async function (newId, oldId) {
            if (!newId || newId === oldId) {
                return;
            }
            this.requestCard_reset();
            this.selfAssignPreviewEnabled = false;
            this.descriptionEditing = false;
            this.changesSaved = false;
            this.changesRefreshRequired = false;
            this.requestSaveError = '';
            await this.fetchRequest();
            if (this.request && this.request.id) {
                markRequestOpened(this.request.id, this.currentUser);
            }
        },
        'request': {
            handler: function (newVal, oldVal) {
                if (!oldVal || !oldVal.id) {
                    return;
                }

                if (this.canEditRequestDetails && this.hasFormChanges && !this.changesBusy && !this.savingRequest) {
                    // Автосохранение с debounce
                    if (this.$store.state.requestCard.autoSaveTimer) {
                        clearTimeout(this.$store.state.requestCard.autoSaveTimer);
                    }

                    const timer = setTimeout(() => {
                        this.requestCard_autoSaveDraft();
                    }, 2000);

                    this.$store.commit('requestCard/setAutoSaveTimer', timer);
                }
            },
            deep: true
        }
    },
    async mounted() {
        this.setupTelegramBackNavigation();
        await this.fetchRequest();
        await this.$nextTick();
        if (this.request && this.request.id) {
            markRequestOpened(this.request.id, this.currentUser);
        }
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.autoRefreshOnResume);
    },
    beforeDestroy() {
        this.teardownTelegramBackNavigation();
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('focus', this.autoRefreshOnResume);
        this.requestCard_reset();
    }
};
</script>

<style src="./RequestCard.css" scoped></style>
